import { useQuery } from "@tanstack/react-query";

import {
  IArticlesTableResponse,
  IRelatedArticles,
} from "../../@types/Articles";
import { articlesSerivce } from "../../services/articlesService";
import { extractErrors } from "../../utils/apiErrorUtils";

const PATIENT_SCOPED_IGNORED_ERROR_CODES = new Set([
  "PatientHospitalIdNotMatch",
  "PatientNotFound",
]);

export function useArticles(hospitalGroupId?: number) {
  const { data, isError, isFetching, refetch, isRefetching } = useQuery<
    IArticlesTableResponse[]
  >({
    queryKey: [`articles-list-${hospitalGroupId}`],
    queryFn: async () => {
      const { data } = await articlesSerivce.gethospitalArticles(
        hospitalGroupId
      );

      return data;
    },
    refetchOnMount: true,
  });

  return {
    articles: data,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

export function useHospitalArticlesForPicker(
  enabled?: boolean,
  patientId?: number
) {
  const isEnabled = enabled !== false;
  const { data, isFetching, isError, refetch } = useQuery<IRelatedArticles[]>({
    queryKey: ["hospital-articles-picker", patientId ?? null],
    queryFn: async () => {
      const response = await articlesSerivce.getAllHospitalArticles(patientId);
      // The shared axios interceptor resolves error responses with `error.response`
      // instead of rejecting, so we explicitly fail the query on non-2xx.
      if (response.status < 200 || response.status >= 300) {
        // When scoping by patientId, the backend may reject with PatientNotFound
        // or PatientHospitalIdNotMatch. These shouldn't break the picker UI —
        // surface an empty list and log instead.
        const errors = extractErrors(response);
        const ignored = errors.some((err) =>
          PATIENT_SCOPED_IGNORED_ERROR_CODES.has(err.code)
        );
        if (ignored) {
          console.error(
            "useHospitalArticlesForPicker: backend rejected patient-scoped fetch",
            { patientId, status: response.status, errors }
          );
          return [];
        }
        throw new Error(`Failed to load articles (status ${response.status})`);
      }
      return Array.isArray(response.data) ? response.data : [];
    },
    staleTime: Infinity,
    enabled: isEnabled,
    retry: false,
  });

  return {
    articles: Array.isArray(data) ? data : [],
    isFetching,
    isError,
    refetch,
  };
}
